import { useQueryClient } from "@tanstack/react-query";
import React from "react";
import type { JSX } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { AppContext, ApplicationContext } from "../../ApplicationContext";
import Footer from "../../components/Footer/Footer";
import NavBar from "../../components/Navigation/NavBar";
import useCurrentUser from "../../hooks/useCurrentUser";
import useGlobalAlert from "../../hooks/useGlobalAlert";
import useQuery from "../../hooks/useQuery";
import i18n from "../../i18n";
import AuthRequest from "../../models/AuthRequest";
import { signIn, signOut } from "../../redux/authSlice";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { AuthErrorResponse } from "../../types/AuthResponse";
import Layout from "../../ui-components/Layout";
import ProgressRing from "../../ui-components/ProgressRing/ProgressRing";
import {
    generateCodeChallenge,
    generateCodeVerifier,
    loadLocationState,
    storeLocationState,
} from "../../utils/authentication";
import { clearPersistedCache } from "../../utils/reactQueryPersistence";

enum AuthAction {
    Verify = "verify",
    Callback = "callback",
    Login = "login",
    Logout = "logout",
}

const actionLabels: Record<AuthAction, string> = {
    [AuthAction.Verify]: i18n.t("labels.verifyingSignIn"),
    [AuthAction.Callback]: i18n.t("labels.progressSigningIn"),
    [AuthAction.Login]: i18n.t("labels.redirectOuthSignIn"),
    [AuthAction.Logout]: i18n.t("labels.signingOut"),
};

export default function AuthHandler(): JSX.Element {
    const params = useParams<{ action?: string }>();
    const action = params.action as AuthAction | undefined;
    const { state } = useLocation();
    const query = useQuery();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const alerts = useGlobalAlert();
    const token = useAppSelector((state) => state.auth.token);
    const userInfo = useCurrentUser();
    const userId = userInfo.data?.sub;

    const queryClient = useQueryClient();

    const appContext = React.useContext<AppContext>(ApplicationContext);
    const basePath = appContext.env.REACT_APP_BASE_PATH;

    async function handleVerification(): Promise<void> {
        // Save previous location
        storeLocationState(state);
        // save in session for callback
        const newVerifier = await generateCodeVerifier();
        sessionStorage.setItem("fss-auth-verifier", newVerifier);
        // Prepare to authorize
        const auth = new AuthRequest();
        const challenger = await generateCodeChallenge(newVerifier);
        const loginLink = await auth.noPromptAuthorizeUrl(challenger);

        // Without the following line, pressing the browser 'back' button at the
        // repo page would incorrectly bring user to '/login/verify' and get stuck
        // there. The following line adds '/home' to browser history so that the
        // 'back' button would return to '/home'. User will still go through the
        // login redirection but at least they're not stuck at '/login/verify' page.
        window.history.replaceState({}, "", `${basePath}/home`);
        window.location.href = loginLink; // redirect to login
    }

    function handleLoginClicked(): void {
        // Remove query string from URL before redirect
        window.history.replaceState({}, "", `/`);
        navigate("/oauth/login", { replace: true });
    }

    const callbackHandledRef = React.useRef<string | null>(null);

    async function handleAuthCallback(): Promise<void> {
        const code = query.get("code");
        if (query.get("error") || !code) {
            // If error exists, verification failed, need to login via prompt
            return navigate("/oauth/login", { replace: true });
        }

        // Prevent double execution in React 18 StrictMode - use code as key
        if (callbackHandledRef.current === code) {
            return;
        }

        const verifier = sessionStorage.getItem("fss-auth-verifier");

        // Else, continue to get token
        if (!token && verifier) {
            callbackHandledRef.current = code;
            try {
                const auth = new AuthRequest();
                const result = await auth.getToken(code, verifier);
                dispatch(signIn(result));
                // Remove from storage
                sessionStorage.removeItem("fss-auth-verifier");

                // Remove query string from URL before redirect
                window.history.replaceState({}, "", `${basePath}/`);
                const to = loadLocationState();
                navigate(to, { replace: true });
            } catch (e) {
                callbackHandledRef.current = null; // Reset on error so user can retry
                const error = e as AuthErrorResponse;
                // When a new tab is opened, the token in Redux is lost and the following error types will be
                // thrown. Automatically redirect to Oxygen login page instead of showing an error message.
                if (error.error === "invalid_grant" || error.error === "no_result_400") {
                    return navigate("/oauth/login", { replace: true });
                }

                alerts.error(error.error_description, {
                    actions: [
                        {
                            id: "login",
                            text: i18n.t("buttons.signIn"),
                            variant: "outlined",
                        },
                    ],
                    onActionClicked: handleLoginClicked,
                });
            }
        }
    }

    async function handleAuthLogin(): Promise<void> {
        // Save in session for callback
        const newVerifier = await generateCodeVerifier();
        sessionStorage.setItem("fss-auth-verifier", newVerifier);
        // Prepare to authorize
        const auth = new AuthRequest();
        const challenger = await generateCodeChallenge(newVerifier);
        const loginLink = await auth.authorizeUrl(challenger);
        window.location.href = loginLink; // redirect to login
    }

    function handleAuthLogout(): void {
        // Remove from storage
        sessionStorage.removeItem("fss-auth-verifier");

        // Clear React Query cache and persisted cache
        queryClient.clear();
        clearPersistedCache(userId);

        dispatch(signOut());
        // Remove query string from URL before redirect
        window.history.replaceState({}, "", `${basePath}/`);
        navigate("/");
    }

    const handledActionRef = React.useRef<string | null>(null);

    React.useEffect(() => {
        // Prevent double execution in React 18 StrictMode - track by action
        if (action && handledActionRef.current === action) {
            return;
        }

        if (action === AuthAction.Verify) {
            handledActionRef.current = action;
            handleVerification();
        } else if (action === AuthAction.Callback) {
            handledActionRef.current = action;
            handleAuthCallback();
        } else if (action === AuthAction.Login) {
            handledActionRef.current = action;
            handleAuthLogin();
        } else if (action === AuthAction.Logout) {
            handledActionRef.current = action;
            handleAuthLogout();
        }

        // Reset ref when action changes
        return () => {
            if (action) {
                handledActionRef.current = null;
            }
        };
    }, [action]);

    const label = action && action in actionLabels ? actionLabels[action as AuthAction] : "Loading...";

    return (
        <Layout header={<NavBar />} footer={<Footer />}>
            <ProgressRing label={label} />
        </Layout>
    );
}
